/**
 * Formats the elapsed time between a given date and now
 * Shows only the most appropriate time unit based on elapsed time:
 * - If < 1 minute: show seconds
 * - If < 1 hour: show minutes
 * - If < 1 day: show hours
 * - Else: show days
 * @param dateString - ISO date string or any valid date string
 * @returns Formatted string in format: "<value><unit>" (e.g., "30s", "5m", "2h", "3d")
 */
export function formatElapsedTime(
  dateString: string,
): string | null | undefined {
  // Handle null and undefined inputs
  if (dateString === null) return null;
  if (dateString === undefined) return undefined;

  // Handle non-string inputs by returning them as strings
  if (typeof dateString !== 'string') {
    return String(dateString);
  }

  // Handle empty string
  if (dateString === '') return '';

  try {
    // Check if the string matches ISO date format pattern
    const isoDatePattern =
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,6})?([+-]\d{2}:\d{2}|Z)?$/;
    if (!isoDatePattern.test(dateString)) {
      return dateString;
    }

    const eventDate = new Date(dateString);

    // Check if the date is invalid
    if (isNaN(eventDate.getTime())) {
      return dateString;
    }

    const now = new Date();
    const diffMs = now.getTime() - eventDate.getTime();

    if (diffMs < 0) return '0s';

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor(diffMs / (1000 * 60));
    const seconds = Math.floor(diffMs / 1000);

    if (days >= 1) {
      return `${days}d`;
    } else if (hours >= 1) {
      return `${hours}h`;
    } else if (minutes >= 1) {
      return `${minutes}m`;
    } else {
      return `${seconds}s`;
    }
  } catch {
    return dateString;
  }
}
